import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ViewChild, OnInit, QueryList, ViewChildren, Input} from '@angular/core';
import { FormGroup, FormControl, Validators, FormArray, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import {GlobalAlertService} from "../../global-alert.service";
import {ButtonCode, GlobalPopup} from '../../global-popup';
import {FormDataService} from "../../../../shared/components/form/services/form-data.service";
import {TranslateService} from "../../../../shared/services/translate/translate.service";
import {DefaultPopupWrapperComponent} from "../default-popup-wrapper/default-popup-wrapper.component";
import {AbstractBasePopupComponent} from '../abstract-base-popup.component';
import {TooltipService} from "../../../../shared/services/tooltip/tooltip.service";
import {LoggerService} from "../../../../shared/services/logger/logger.service";
import {GlobalModel} from "../../../../shared/services/state/global.model";
import {GlobalPopup2faService} from "./global-popup-2fa.service";
import {take} from "rxjs/operators";
import {SessionService} from "../../../../shared/services/storage/session.service";
import {Router} from "@angular/router";


@Component ({
    selector: 'global-popup-2fa-component',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: "./global-popup-2fa.component.html",
    styleUrls: ['./global-popup-2fa.component.scss']
})

export class GlobalPopup2faComponent extends AbstractBasePopupComponent implements OnInit {
    @Input() canIgnorePopup: boolean = true;
    @ViewChild('wrapper', {static: false}) wrapper: DefaultPopupWrapperComponent;
    @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;

    public hasSendEmail: boolean = false;
    public hasSendCode: boolean = false;
    public isCodeCorrect: boolean = false;
    public timeTillEmailResend:number = 0;
    public formError:string = '';
    private static secondsToWaitForEmailResend: number = 30;

    userForm = new FormGroup({
        currentUsername: new FormControl({ value: this.userData.username, disabled: true }, [Validators.required]),
        currentEmail: new FormControl({ value: this.userData.email, disabled: true }, [Validators.required]),
        newEmail: new FormControl({ value: this.userData.email, disabled: this.hasSendEmail }, [Validators.required, Validators.email]),
        code: new FormArray(
            Array.from({ length: 6 }, () =>
                new FormControl('', [Validators.pattern(/^[0-9]$/)])
            ),
            []
        )
    });

    public constructor(protected cd:ChangeDetectorRef, protected globalAlertService:GlobalAlertService, protected formDataService:FormDataService, protected elementRef:ElementRef, protected ts:TranslateService, protected tooltipService:TooltipService, protected logger:LoggerService,public model:GlobalModel, private popup2faService:GlobalPopup2faService, private sessionService:SessionService, private router:Router) {
        super(cd, globalAlertService, elementRef, ts, tooltipService, logger);
    }

    ngOnInit() {
        this.setIsLoading(false);
    }

    get userData():any{
        return this.model.user.value;
    }

    get saveButtonLabel():string{
        let buttonText:string = this.ts.translate('2fa.send-email');
        if(this.hasSendEmail){
            buttonText = this.ts.translate('2fa.popup-save-changes');
        }
        if(this.hasSendCode && this.isCodeCorrect){
            buttonText = this.ts.translate('close');
        }
        return buttonText;
    }

    get codeControls() {
        return (this.userForm.get('code') as FormArray).controls;
    }

    public setIsLoading(loading:boolean):void{
        this.isLoading = loading
        this.cd.detectChanges()
    }

    public resendEmail():void{
        this.hasSendCode = false;
        this.resetCode();
        this.startTimer();
        const newEmailValue = this.userForm.get('newEmail').value;
        this.popup2faService.save2faForm(newEmailValue, '').pipe(take(1)).subscribe({
            next:(val)=>{
                this.formError = '';
            },
            error:(err)=>{
                console.error(err);
            }
        });
    }

    public sendCode():void{
        if(this.userForm.valid){
            const newEmailValue = this.userForm.get('newEmail').value;
            const codeValue = this.userForm.get('code')?.value.join('');

            this.popup2faService.save2faForm(newEmailValue,codeValue).pipe(take(1)).subscribe({
                next:(val)=>{
                    if(val.message == 'invalid' && codeValue == ''){
                        this.formError = this.ts.translate('2fa.popup-email-not-allowed');
                    } else {
                        this.formError = '';
                        if(!this.hasSendEmail){
                            this.hasSendEmail = true;
                            this.makeCodeRequired()

                            this.userForm.get('newEmail')?.disable();
                            this.startTimer();

                            setTimeout(()=>{
                                const codeArray = this.userForm.get('code') as FormArray;
                                const firstEmpty = codeArray.controls.findIndex(ctrl => !ctrl.value);
                                if (firstEmpty !== -1) {
                                    this.otpInputs.toArray()[firstEmpty].nativeElement.focus();
                                }
                            })
                        } else {
                            this.hasSendCode = true;
                            this.isCodeCorrect = val.message == 'valid';

                            if(!this.isCodeCorrect){
                                this.resetCode()
                            } else {
                                this.userForm.get('code').disable();
                            }
                        }
                    }
                    this.cd.detectChanges();
                },
                error:(err)=>{
                    console.error(err);
                }
            })
        }
        if(this.hasSendCode && this.isCodeCorrect){
            this.closePopup(null)
        }
    }

    public onPaste(event: ClipboardEvent) {
        event.preventDefault();
        const pasteData = event.clipboardData?.getData('text')?.trim() || '';
        const digits = pasteData.replace(/\D/g, '');

        const codeArray = this.userForm.get('code') as FormArray;
        digits.split('').forEach((d, i) => {
            if (i < codeArray.length) {
                codeArray.at(i).setValue(d);
            }
        });

        const lastIndex = Math.min(digits.length, codeArray.length) - 1;
        if (lastIndex >= 0) {
            this.otpInputs.toArray()[lastIndex].nativeElement.focus();
        }
    }

    public onInput(event: any, index: number) {
        const input = event.target as HTMLInputElement;
        const value = input.value;

        if (!/^[0-9]$/.test(value)) {
            input.value = '';
            this.codeControls[index].setValue('');
            return;
        }

        if (value && index < this.otpInputs.length - 1) {
            this.otpInputs.toArray()[index + 1].nativeElement.focus();
        }
    }

    public onKeyDown(event: KeyboardEvent, index: number) {
        const input = event.target as HTMLInputElement;

        if (event.key === 'Backspace' && !input.value && index > 0) {
            this.otpInputs.toArray()[index - 1].nativeElement.focus();
        }
    }

    private resetCode() {
        const codeArray = this.userForm.get('code') as FormArray;
        codeArray.reset();
        this.otpInputs.first.nativeElement.focus();
    }

    private startTimer():void{
        this.timeTillEmailResend = GlobalPopup2faComponent.secondsToWaitForEmailResend;
        let countdownTimer = setInterval(()=>{
            this.timeTillEmailResend--;
            if(this.timeTillEmailResend === 0){
                clearInterval(countdownTimer)
            }
            this.cd.detectChanges()
        }, 1000)
    }

    private twoFaValidator(length: number = 6): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!(control instanceof FormArray)) return null;

            const code = control.controls.map(c => c.value).join('');
            const regex = new RegExp(`^[0-9]{${length}}$`);

            return regex.test(code) ? null : { invalidTwoFa: true };
        };
    }

    private makeCodeRequired() {
        const codeArray = this.userForm.get('code') as FormArray;
        codeArray.setValidators([this.twoFaValidator(6)]);
        codeArray.updateValueAndValidity();
    }

    handlePopupAction(event: MouseEvent, alert: GlobalPopup, button: any): void {
        if(button.code == ButtonCode.CLOSE ){
            if(this.isCodeCorrect){
                this.router.navigate(['/settings/user-profile']);
                this.onPopupAction.emit({event:event, alert:alert, button:button});
            } else {
                if(this.canIgnorePopup){
                    this.sessionService.setItem(SessionService.KEY_2FA_POPUP_CLOSED, 'true');
                    this.onPopupAction.emit({event:event, alert:alert, button:button});
                }
            }
        }
    }

    public closePopup(event:any):void {
        this.doDefaultCloseHandling(event, this.config && !this.config.disabled);
    }
}
